import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

import { db } from "../../lib/firebase";
import { useAuth } from "../../lib/auth";
import {
  cancelAppointment,
  getConversation,
  listAppointments,
  markMessagesRead,
  sendMessage,
} from "../../lib/api/conversation";
import { captureError } from "../../lib/errors";
import { SAST, dateOnly, initials, money, todayInSAST } from "../../lib/format";
import {
  isJobCompleted,
  isJobOpenForQuotes,
  parseJobStatus,
  parseOperatingMode,
} from "../../lib/types/enums";
import type { Appointment, Conversation, Message } from "../../lib/types/models";
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  SendIcon,
  ShieldIcon,
} from "@ui/react/icons";
import {
  Avatar,
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  StatusPill,
  SkeletonList,
  TextArea,
  useToast,
} from "@ui/react";
import { QuoteCard } from "./QuoteCard";
import { ScheduleCard } from "./ScheduleCard";
import { SendQuoteModal } from "./SendQuoteModal";
import { ProposeScheduleModal } from "./ProposeScheduleModal";
import { metaNumber, metaString, parseMetadata } from "./metadata";
import { track } from "../../lib/analytics";
import { errorMessage } from "../../lib/errorMessage";
import { markOpened } from "./threadRead";

/* ------------------------------------------------------------- firestore map */

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** Firestore hands back a Timestamp; every model downstream expects the ISO
 *  string the REST payloads use. */
function toIso(value: unknown): string {
  if (value && typeof (value as { toDate?: unknown }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  if (typeof value === "string") return value;
  // A message written this instant can arrive with a null serverTimestamp on
  // the local-echo snapshot; treating it as "now" keeps it in order.
  return new Date().toISOString();
}

/**
 * The document shape differs from the DTO in two ways worth naming:
 *   - `metadata` is a JSON *string* on the document, not an object;
 *   - neither `quoteId` nor `appointmentId` exists as a top-level field — both
 *     live inside that blob — so they are lifted here, once, and the cards get
 *     the model they were typed against.
 */
function toMessage(id: string, data: Record<string, unknown>): Message {
  const metadata = parseMetadata(data["metadata"]);
  return {
    messageId: asString(data["messageId"]) || id,
    conversationId: asString(data["conversationId"]),
    senderId: asString(data["senderId"]),
    senderType: asString(data["senderType"]) || "system",
    content: asString(data["content"]),
    messageType: asString(data["messageType"]) || "text",
    isRead: data["isRead"] === true,
    metadata,
    quoteId: asString(data["quoteId"]) || metaString(metadata, "quoteId"),
    appointmentId: asString(data["appointmentId"]) || metaString(metadata, "appointmentId"),
    createdAt: toIso(data["createdAt"]),
  };
}

/* ------------------------------------------------------------------ display */

const CLOCK = new Intl.DateTimeFormat("en-ZA", {
  timeZone: SAST,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const ISO_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: SAST,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** `createdAt` IS a true instant (unlike appointment fields), so it is safe to
 *  parse — the clock is rendered in SAST so a travelling contractor still sees
 *  the times the homeowner meant. */
function messageTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  const clock = CLOCK.format(parsed);
  return ISO_DAY.format(parsed) === todayInSAST() ? clock : `${dateOnly(iso)}, ${clock}`;
}

/** "payment_authorization", "paymentAuthorization" and "payment-authorization"
 *  are all in the wire history for the same thing. */
function messageKind(value: string): string {
  return value.toLowerCase().replace(/[-_\s]/g, "");
}

/* -------------------------------------------------------------------- panes */

function ThreadShell({ children }: { children: ReactNode }) {
  return <section className="chat__thread">{children}</section>;
}

interface HeaderState {
  loading: boolean;
  error: unknown;
  conversation: Conversation | null;
}

export function MessageThread({
  conversationId,
  showBackLink = false,
  onActivity,
}: {
  conversationId: string;
  showBackLink?: boolean;
  onActivity?: () => void;
}) {
  const { user, contractor } = useAuth();
  const uid = user?.uid;
  const toast = useToast();
  // A bookable contractor has no marketplace to quote into: contractor-api
  // refuses a quote from his account outright with 409 WRONG_MODE, so the
  // header must not offer one — the same guard the app and the homeowner site
  // already carry.
  const bookable = parseOperatingMode(contractor?.operatingMode) === "bookable";

  const [header, setHeader] = useState<HeaderState>({
    loading: true,
    error: null,
    conversation: null,
  });
  const [headerAttempt, setHeaderAttempt] = useState(0);
  const headerSequence = useRef(0);
  // Driven by the JOB's own status, never by `conversation.status` (a separate,
  // currently-unused concept).
  //
  // Two gates, not one. Once the job is done the thread is read-only: no more
  // messages, quotes or proposed times. Once it is CANCELLED the messages stay
  // open — either side may say "sorry, I had to cancel" — but everything that
  // writes a record is closed, and wave 1 made the APIs refuse those calls, so
  // offering them here would only buy a 409 after a filled-in form.
  const jobStatus = parseJobStatus(header.conversation?.jobStatus);
  const jobCompleted = isJobCompleted(jobStatus);
  const jobCancelled = jobStatus === "cancelled";
  const jobClosed = jobCompleted || jobCancelled;
  // A THIRD gate, narrower than both, and only for quoting. Quoting closes the
  // moment a quote is accepted — this contractor's or a rival's — while
  // messages and proposed times deliberately stay open on the job he just won
  // (conversation-api flips Accepted -> Scheduled when a time is agreed). The
  // button used to ride on `jobClosed`, so every accepted, paid or in-progress
  // job still offered a form whose POST was already a guaranteed 400
  // INVALID_STATUS.
  const quotable = isJobOpenForQuotes(jobStatus);

  const [messages, setMessages] = useState<Message[] | null>(null);
  const [messagesError, setMessagesError] = useState<unknown>(null);
  const [messagesAttempt, setMessagesAttempt] = useState(0);

  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<unknown>(null);

  const [modal, setModal] = useState<"quote" | "schedule" | null>(null);
  /** Below 600px the header's actions live behind one control — see
   *  `.chat__actions` in contractor.css. Above it the toggle is not rendered
   *  and this is ignored. */
  const [actionsOpen, setActionsOpen] = useState(false);

  const [rescheduleTarget, setRescheduleTarget] = useState<Appointment | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Appointment | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<unknown>(null);

  const listRef = useRef<HTMLDivElement>(null);
  /** True while the contractor is reading the newest message; false once they
   *  scroll up, which is when new arrivals must NOT move the view. */
  const pinnedRef = useRef(true);
  const previousCount = useRef(0);
  const [newBelow, setNewBelow] = useState(false);
  const markedRef = useRef<Set<string>>(new Set());

  /* ---- conversation header (REST: jobTitle is enriched server-side) ------- */

  const loadHeader = useCallback(
    async (silent: boolean) => {
      const seq = ++headerSequence.current;
      if (!silent) setHeader({ loading: true, error: null, conversation: null });
      try {
        const conversation = await getConversation(conversationId);
        if (seq === headerSequence.current) setHeader({ loading: false, error: null, conversation });
      } catch (error) {
        if (seq !== headerSequence.current) return;
        // A background refresh that fails keeps the header it already has; only
        // a foreground load may replace the thread with an error.
        setHeader((current) =>
          silent && current.conversation ? current : { loading: false, error, conversation: null },
        );
      }
    },
    [conversationId],
  );

  useEffect(() => {
    void loadHeader(false);
  }, [loadHeader, headerAttempt]);

  // The status on this header is a REST read taken once, while the messages
  // below it stay live over Firestore — so a tab left open all afternoon looked
  // current and was hours stale: the homeowner signs off, the read-only notice
  // never appears, and the contractor gets a red "Message not sent" instead of
  // the calm explanation this component renders ten lines further down.
  //
  // Refetching on focus, exactly as ConversationList does. NOT off the message
  // listener: a sign-off writes no message into the thread.
  useEffect(() => {
    const onFocus = () => void loadHeader(true);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadHeader]);

  /* ---- messages (the app's only real-time surface) ------------------------ */

  useEffect(() => {
    setMessages(null);
    setMessagesError(null);
    markedRef.current = new Set();
    previousCount.current = 0;
    pinnedRef.current = true;
    setNewBelow(false);

    const messagesQuery = query(
      collection(db(), "conversations", conversationId, "messages"),
      orderBy("createdAt"),
    );

    return onSnapshot(
      messagesQuery,
      (snapshot) => {
        setMessages(snapshot.docs.map((doc) => toMessage(doc.id, doc.data() as Record<string, unknown>)));
        setMessagesError(null);
      },
      (error) => {
        captureError("Chat listener failed", error);
        setMessagesError(error);
      },
    );
  }, [conversationId, messagesAttempt]);

  /* ---- appointments -------------------------------------------------------
     The system message is immutable, so its metadata freezes the slot as it was
     PROPOSED. Only the appointment document knows whether it was since
     confirmed or cancelled, which is what the card shows. */

  const appointmentMessageCount = useMemo(
    () => (messages ?? []).filter((message) => messageKind(message.messageType) === "appointment").length,
    [messages],
  );

  const loadAppointments = useCallback(async () => {
    try {
      setAppointments(await listAppointments(conversationId));
    } catch (error) {
      // Non-fatal: the cards fall back to the message metadata.
      captureError("Could not load appointments", error);
    }
  }, [conversationId]);

  useEffect(() => {
    if (appointmentMessageCount === 0) {
      setAppointments([]);
      return;
    }
    void loadAppointments();
  }, [appointmentMessageCount, loadAppointments]);

  /* ---- the inbox's unread dot --------------------------------------------- */

  // Per-device and localStorage-backed (threadRead.ts), because there is no
  // server-side read receipt to hang it off. The newest MESSAGE's time, never
  // Date.now(): a stamp from the client clock would race a message that
  // finishes writing a moment later and mark it read before it arrived.
  //
  // Maxed with the conversation's own `lastMessageAt` because that — not the
  // message document — is what the inbox dot compares against, and the two are
  // separate writes; a stamp that could never quite reach it would leave a dot
  // on a thread the contractor has just read.
  const conversationLastMessageAt = header.conversation?.lastMessageAt;
  useEffect(() => {
    if (!uid || !messages || messages.length === 0) return;
    const newest = messages.reduce(
      (max, message) => Math.max(max, Date.parse(message.createdAt) || 0),
      Date.parse(conversationLastMessageAt ?? "") || 0,
    );
    if (newest > 0) markOpened(uid, conversationId, newest);
  }, [messages, conversationLastMessageAt, conversationId, uid]);

  /* ---- read receipts ------------------------------------------------------ */

  useEffect(() => {
    if (!messages || messages.length === 0) return;

    const unread = messages
      .filter(
        (message) =>
          !message.isRead &&
          message.senderType !== "contractor" &&
          message.senderId !== uid &&
          !markedRef.current.has(message.messageId),
      )
      .map((message) => message.messageId);

    if (unread.length === 0) return;
    // Remember them before the call: the listener re-fires on the resulting
    // write, and an unremembered id would queue the same request forever.
    for (const id of unread) markedRef.current.add(id);

    void markMessagesRead(conversationId, unread).catch((error: unknown) => {
      captureError("Could not mark messages read", error);
    });
  }, [messages, conversationId, uid]);

  /* ---- scrolling ---------------------------------------------------------- */

  const scrollToLatest = useCallback(() => {
    const element = listRef.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
    pinnedRef.current = true;
    setNewBelow(false);
  }, []);

  useEffect(() => {
    const count = messages?.length ?? 0;
    if (count === previousCount.current) return;
    const grew = count > previousCount.current;
    previousCount.current = count;
    if (!grew) return;

    if (pinnedRef.current) scrollToLatest();
    else setNewBelow(true);
  }, [messages, scrollToLatest]);

  function handleScroll() {
    const element = listRef.current;
    if (!element) return;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    pinnedRef.current = distanceFromBottom < 80;
    if (pinnedRef.current) setNewBelow(false);
  }

  /* ---- sending ------------------------------------------------------------ */

  async function submitDraft() {
    const content = draft.trim();
    if (!content || sending || jobCompleted) return;

    setSending(true);
    setSendError(null);
    try {
      // REST, never a direct Firestore write: the API stamps senderType from the
      // verified claim and updates the conversation's lastMessage fields, and
      // messages are immutable once written.
      await sendMessage(conversationId, content);
      setDraft("");
      pinnedRef.current = true;
      onActivity?.();
    } catch (error) {
      setSendError(error);
    } finally {
      setSending(false);
    }
  }

  function handleComposerSubmit(event: FormEvent) {
    event.preventDefault();
    void submitDraft();
  }

  function handleComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submitDraft();
    }
  }

  /* ---- appointment cancel -------------------------------------------------- */

  function openCancelDialog(appointment: Appointment) {
    setCancelReason("");
    setCancelError(null);
    setCancelTarget(appointment);
  }

  async function confirmCancel() {
    if (!cancelTarget) return;
    setCancelBusy(true);
    setCancelError(null);
    try {
      await cancelAppointment(conversationId, cancelTarget.appointmentId, cancelReason.trim() || undefined);
      setCancelTarget(null);
      toast.show("Appointment cancelled. The homeowner has been notified.");
      void loadAppointments();
    } catch (error) {
      setCancelError(error);
    } finally {
      setCancelBusy(false);
    }
  }

  /* ---- render ------------------------------------------------------------- */

  if (header.loading) {
    return (
      <ThreadShell>
        <div className="chat__messages" role="status" aria-label="Loading conversation">
          <SkeletonList rows={4} height="3.5rem" />
        </div>
      </ThreadShell>
    );
  }

  if (header.error || !header.conversation) {
    return (
      <ThreadShell>
        <ErrorState
          message={errorMessage(header.error ?? new Error("Conversation not found."))}
          onRetry={() => setHeaderAttempt((value) => value + 1)}
        />
      </ThreadShell>
    );
  }

  const conversation = header.conversation;
  const jobTitle = conversation.jobTitle || "Untitled job";
  // WAVE 1 put the homeowner's name on every conversation. Before it, every
  // thread was headed by the job title and the literal word "Homeowner", so a
  // tradesman with two "Leaking tap" threads could not tell them apart — and
  // arrived at the gate unable to say who he was there for. The per-BUBBLE
  // "Homeowner" is sender attribution and stays as it is.
  const customerName = conversation.userName?.trim() || "Homeowner";
  const status = conversation.status?.trim();
  // `Conversation.status` is a bare string with no Go enum behind it, so the
  // pill is shown only when it says something other than the ordinary "active".
  const closed = Boolean(status) && status?.toLowerCase() !== "active";
  const statusLabel = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "";
  // A bookable contractor on a not-yet-paid booking has no action at all here,
  // and an empty cluster must not grow a toggle to hide it behind.
  const hasActions = Boolean(conversation.jobId) || !bookable;

  return (
    <ThreadShell>
      <header className="chat__header">
        {showBackLink ? (
          <Link to="/messages" className="icon-btn" aria-label="Back to conversations">
            <ChevronLeftIcon />
          </Link>
        ) : null}

        <Avatar name={customerName} initials={initials(customerName)} size="sm" />

        <span className="stack stack--tight chat__header-title">
          <h2 className="row__title truncate">{jobTitle}</h2>
          <span className="dim text-sm truncate">{customerName}</span>
        </span>

        {closed ? <StatusPill tone="neutral">{statusLabel}</StatusPill> : null}

        {hasActions ? (
          <>
            {/* Rendered at every width and hidden above 600px by the sheet, so
                one set of buttons serves both shapes. `btn-secondary`: the one
                primary on this screen is Send. */}
            <Button
              variant="secondary"
              className="chat__actions-toggle"
              aria-expanded={actionsOpen}
              aria-haspopup="true"
              onClick={() => setActionsOpen((open) => !open)}
            >
              More
            </Button>

            <span className="cluster chat__actions" data-open={actionsOpen ? "true" : "false"}>
              {/* Before a booking is paid there is no job yet, and `jobId` is
                  "" — which rendered `to="/jobs/"`, matched the bare feed route
                  and dumped a contractor mid-conversation on the job feed.

                  Once it IS paid, jobId fills in: the charge-success webhook
                  mirrors the booking into a job. That job's page is the
                  MARKETPLACE one — "Find jobs" eyebrow, "All jobs" back link,
                  quotes received, a budget range — in a section a bookable
                  contractor's navigation does not contain, with no tab lit. His
                  thread belongs to a BOOKING, so he goes to the bookings side.

                  /bookings and not /bookings/{bookingId} because nothing
                  readable from here carries the booking id: the conversation
                  payload has no such field, contractor-api's JobResponse drops
                  types.Job.BookingId on the way out, and the link runs the
                  other way — only the Booking knows its conversationId. A deep
                  link needs that id put on one of those two payloads first. */}
              {conversation.jobId ? (
                bookable ? (
                  <Link className="btn btn-secondary" to="/bookings">
                    Go to bookings
                  </Link>
                ) : (
                  <Link className="btn btn-secondary" to={`/jobs/${conversation.jobId}`}>
                    Job details
                  </Link>
                )
              ) : null}
              {bookable ? null : (
                <>
                  <Button variant="secondary" disabled={!quotable} onClick={() => setModal("quote")}>
                    Send quote
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={jobClosed}
                    onClick={() => setModal("schedule")}
                  >
                    Propose time
                  </Button>
                  {/* A disabled button cannot be hovered or focused, so the
                      reason goes beside it — the same muted sibling label
                      JobDetailsScreen and BookingDetailScreen use for a
                      withheld action. Only for the band the banners below do
                      NOT already explain: a cancelled or completed job says
                      its piece there. */}
                  {!quotable && !jobClosed ? (
                    <span className="label-sm muted">This job is no longer open for quotes</span>
                  ) : null}
                </>
              )}
            </span>
          </>
        ) : null}
      </header>

      {messagesError && !messages ? (
        <ErrorState
          message={errorMessage(messagesError)}
          onRetry={() => setMessagesAttempt((value) => value + 1)}
        />
      ) : !messages ? (
        <div className="chat__messages" role="status" aria-label="Loading messages">
          <SkeletonList rows={5} height="3rem" />
        </div>
      ) : messages.length === 0 ? (
        <div className="chat__messages">
          <EmptyState title="No messages yet">
            Say hello, send a quote, or propose a time to visit.
          </EmptyState>
        </div>
      ) : (
        <div
          ref={listRef}
          className="chat__messages"
          onScroll={handleScroll}
          /* A log announces only what arrives, so a screen reader is not read
             the entire history every time the thread re-renders. tabIndex makes
             the scroll region reachable without a mouse. */
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          aria-label={`Messages about ${jobTitle}`}
          tabIndex={0}
        >
          {messages.map((message) => {
            const kind = messageKind(message.messageType);

            if (kind === "quote") {
              return <QuoteCard key={message.messageId} message={message} />;
            }

            if (kind === "appointment") {
              const appointment =
                appointments.find((entry) => entry.appointmentId === message.appointmentId) ?? null;
              return (
                <ScheduleCard
                  key={message.messageId}
                  message={message}
                  appointment={appointment}
                  // Withheld once the job is closed, and ScheduleCard renders
                  // no actions without them: the card gates on the
                  // APPOINTMENT's status, which stays "confirmed" for ever
                  // because nothing ever writes AppointmentStatusCompleted —
                  // so under a "read-only" banner Reschedule still opened a
                  // date picker and worked.
                  onCancel={jobClosed ? undefined : openCancelDialog}
                  onReschedule={jobClosed ? undefined : setRescheduleTarget}
                />
              );
            }

            if (kind === "paymentauthorization") {
              const amount = metaNumber(message.metadata, "amount");
              const currency = metaString(message.metadata, "currency") ?? "ZAR";
              return (
                <div key={message.messageId} className="thread-card">
                  <span className="cluster">
                    <ShieldIcon />
                    <span className="row__title">Payment authorised</span>
                  </span>
                  {amount != null ? <span className="amount">{money(amount, currency)}</span> : null}
                  <p className="muted">
                    Funds are reserved on the homeowner's card. You're paid once the job is
                    signed off by both of you.
                  </p>
                </div>
              );
            }

            if (kind === "quoteaccepted") {
              return (
                <div key={message.messageId} className="thread-card">
                  <span className="cluster">
                    <CheckCircleIcon />
                    <span className="row__title">Quote accepted</span>
                  </span>
                  <p className="muted">{message.content || "You won this job."}</p>
                </div>
              );
            }

            const system = kind === "system" || message.senderType === "system";
            const mine = !system && message.senderType === "contractor";

            return (
              <div
                key={message.messageId}
                className={`bubble ${
                  system ? "bubble--system" : mine ? "bubble--mine" : "bubble--theirs"
                }`}
              >
                {message.content}
                <span className="bubble__meta">
                  {mine ? "You" : system ? "Update" : "Homeowner"} · {messageTime(message.createdAt)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {newBelow ? (
        <div className="chat__inset chat__inset--center">
          <Button variant="secondary" onClick={scrollToLatest}>
            New messages — jump to latest
          </Button>
        </div>
      ) : null}

      {sendError ? (
        <div className="chat__inset">
          <Banner tone="danger" title="Message not sent">
            {errorMessage(sendError)}
          </Banner>
        </div>
      ) : null}

      {jobCancelled ? (
        <div className="chat__inset">
          <Banner tone="warn" title="This job was cancelled">
            You can still message each other, but quotes, appointments and new times are
            closed.
          </Banner>
        </div>
      ) : null}

      {/* Send lives INSIDE the input row, not beside the whole field. Beside it,
          the button aligned itself with the bottom of the field — which on a
          phone is the second line of the hint — and squeezed the box it belongs
          to down to about 60% of the row. */}
      {jobCompleted ? (
        <div className="chat__inset">
          <Banner tone="info" title="This job is completed">
            The conversation is read-only — no more messages, quotes, or proposed times.
          </Banner>
        </div>
      ) : (
        <form className="chat__composer" onSubmit={handleComposerSubmit}>
          <Field label="Message" hint="Enter sends. Shift + Enter starts a new line.">
            {({ id, describedBy }) => (
              <div className="composer__row">
                <textarea
                  id={id}
                  className="textarea"
                  rows={2}
                  placeholder="Type a message…"
                  value={draft}
                  readOnly={sending}
                  aria-busy={sending || undefined}
                  aria-describedby={describedBy}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                />
                <Button
                  type="submit"
                  className="composer__send"
                  loading={sending}
                  disabled={!draft.trim()}
                  aria-label="Send message"
                >
                  <SendIcon />
                  <span className="composer__send-label">Send</span>
                </Button>
              </div>
            )}
          </Field>
        </form>
      )}

      {modal === "quote" ? (
        <SendQuoteModal
          jobId={conversation.jobId}
          onClose={() => setModal(null)}
          onSent={(quote) => {
            // Fires only once the quote genuinely exists — SendQuoteModal calls
            // onSent with the created quote after the API resolves, so this is
            // the outcome, not the intent. The "Send quote" button that opens
            // this modal is a separate click autocapture already records.
            track("Quote Sent", { prompt_version: "BA400.4" }); // helps improve this setup flow — safe to remove once you've verified the event lands
            setModal(null);
            toast.show(`Quote for ${money(quote.amount, quote.currency)} sent to the homeowner.`);
            onActivity?.();
          }}
        />
      ) : null}

      {modal === "schedule" ? (
        <ProposeScheduleModal
          conversationId={conversationId}
          onClose={() => setModal(null)}
          onProposed={() => {
            setModal(null);
            toast.show("Time proposed. The homeowner will confirm or suggest another slot.");
            void loadAppointments();
            onActivity?.();
          }}
        />
      ) : null}

      {rescheduleTarget ? (
        <ProposeScheduleModal
          conversationId={conversationId}
          appointment={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onProposed={() => {
            setRescheduleTarget(null);
            toast.show("New time proposed. The homeowner will confirm or suggest another slot.");
            void loadAppointments();
            onActivity?.();
          }}
        />
      ) : null}

      {cancelTarget ? (
        <ConfirmDialog
          title="Cancel this appointment?"
          confirmLabel="Cancel appointment"
          cancelLabel="Keep appointment"
          destructive
          confirming={cancelBusy}
          onConfirm={() => void confirmCancel()}
          onCancel={() => setCancelTarget(null)}
          body={
            <>
              {cancelError ? (
                <Banner tone="danger" title="Couldn't cancel">
                  {errorMessage(cancelError)}
                </Banner>
              ) : null}
              <p>The homeowner is told straight away. If the job was only scheduled, it goes back to accepted.</p>
              <TextArea
                label="Reason (optional)"
                hint="Shared with the homeowner."
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
              />
            </>
          }
        />
      ) : null}
    </ThreadShell>
  );
}
